import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import { StatsCard, LoadingState } from '../../../components/admin/shared';
import { FileText, Clock, CheckCircle, XCircle, TrendingDown } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const ServiceAnalytics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || 'overseas');

  const services = [
    { value: 'overseas', label: 'Overseas Education' },
    { value: 'career', label: 'Career Counseling' },
    { value: 'skills', label: 'Skill Development' },
    { value: 'test-prep', label: 'Test Preparation' },
    { value: 'scholarships', label: 'Scholarship Finder' },
    { value: 'visa', label: 'Visa Assistance' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'pre-departure', label: 'Pre-Departure' },
    { value: 'post-arrival', label: 'Post-Arrival' },
    { value: 'alumni', label: 'Alumni Network' }
  ];

  useEffect(() => {
    loadServiceAnalytics();
  }, [selectedService]);

  const loadServiceAnalytics = async () => {
    setLoading(true);
    try {
      const response = await educentialApi.get(`/api/admin/reports/services?service=${selectedService}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading service analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceChange = (service) => {
    setSelectedService(service);
    setSearchParams({ service });
  };

  if (loading) {
    return (
      <AdminLayout title="Service Analytics">
        <LoadingState message="Loading service analytics..." />
      </AdminLayout>
    );
  }

  const { applications, documents, conversionFunnel } = analytics || {};

  return (
    <AdminLayout title="Service Analytics">
      <div className="p-6 space-y-6">
        {/* Header with Service Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Service Analytics</h2>
            <p className="text-gray-600 mt-1">Detailed metrics for service performance</p>
          </div>
          <select
            value={selectedService}
            onChange={(e) => handleServiceChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {services.map(service => (
              <option key={service.value} value={service.value}>
                {service.label}
              </option>
            ))}
          </select>
        </div>

        {/* Check if service has data */}
        {selectedService !== 'overseas' ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-yellow-800 font-medium">
              {services.find(s => s.value === selectedService)?.label} service is not yet active.
            </p>
            <p className="text-yellow-700 text-sm mt-2">
              Analytics will be available once the service is activated and applications are received.
            </p>
          </div>
        ) : (
          <>
            {/* Application Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatsCard title="Total" value={applications?.total || 0} icon={FileText} />
              <StatsCard title="Draft" value={applications?.draft || 0} icon={FileText} />
              <StatsCard title="Submitted" value={applications?.submitted || 0} icon={Clock} />
              <StatsCard title="Completed" value={applications?.completed || 0} icon={CheckCircle} />
              <StatsCard title="Rejected" value={applications?.rejected || 0} icon={XCircle} />
            </div>

            {/* Conversion Funnel */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Application Funnel</h3>
              <div className="space-y-4">
                {conversionFunnel && conversionFunnel.map((stage, index) => {
                  const maxCount = conversionFunnel[0]?.count || 1;
                  const widthPercentage = (stage.count / maxCount) * 100;

                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">
                            {index + 1}
                          </div>
                          <span className="text-sm font-medium text-gray-900">{stage.stage}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-gray-900">{stage.count}</span>
                          <span className="text-xs text-gray-500 ml-2">({stage.percentage.toFixed(1)}%)</span>
                        </div>
                      </div>
                      <div className="relative">
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${widthPercentage}%` }}
                          ></div>
                        </div>
                        {index < conversionFunnel.length - 1 && (
                          <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2">
                            <TrendingDown className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Funnel Summary */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-600 font-medium">Overall Conversion</p>
                    <p className="text-2xl font-bold text-blue-900 mt-1">
                      {conversionFunnel && conversionFunnel[0]?.count > 0
                        ? ((conversionFunnel[conversionFunnel.length - 1]?.count / conversionFunnel[0]?.count) * 100).toFixed(1)
                        : 0}%
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-green-600 font-medium">Drop-off Rate</p>
                    <p className="text-2xl font-bold text-green-900 mt-1">
                      {conversionFunnel && conversionFunnel[0]?.count > 0
                        ? (100 - ((conversionFunnel[conversionFunnel.length - 1]?.count / conversionFunnel[0]?.count) * 100)).toFixed(1)
                        : 0}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Document Stats */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Review Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">{documents?.total || 0}</div>
                  <div className="text-sm text-gray-600 mt-1">Total Documents</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-600">{documents?.pending || 0}</div>
                  <div className="text-sm text-gray-600 mt-1">Pending</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">{documents?.approved || 0}</div>
                  <div className="text-sm text-gray-600 mt-1">Approved</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600">{documents?.rejected || 0}</div>
                  <div className="text-sm text-gray-600 mt-1">Rejected</div>
                </div>
              </div>
            </div>

            {/* Performance Insights */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-blue-900">Average Processing Time</p>
                    <p className="text-xs text-blue-700 mt-1">Time from submission to completion</p>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {analytics?.avgProcessingTime || 0} days
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-green-900">Success Rate</p>
                    <p className="text-xs text-green-700 mt-1">Applications completed successfully</p>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {applications?.total > 0
                      ? ((applications.completed / applications.total) * 100).toFixed(1)
                      : 0}%
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-red-900">Rejection Rate</p>
                    <p className="text-xs text-red-700 mt-1">Applications rejected or declined</p>
                  </div>
                  <div className="text-2xl font-bold text-red-600">
                    {applications?.total > 0
                      ? ((applications.rejected / applications.total) * 100).toFixed(1)
                      : 0}%
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ServiceAnalytics;
